/**
 * Global Footprint — Section 05 of the approved IR content sheet.
 *
 * Marker placement is derived from real geographic coordinates and projected
 * onto the globe at render time (d3-geo orthographic projection), so the pins
 * stay correct as the sphere rotates to centre the selected country.
 */

/** Longitude / latitude the sphere faces before anything is selected. */
export const GLOBE_INITIAL_VIEW: [number, number] = [55, 14];

/**
 * Indicative sales-coverage points — decorative only, they carry no label and
 * are hidden from assistive tech. They stand in for the "50+ markets served"
 * claim until an approved market list is supplied.
 */
export const footprintSalesMarkets: [number, number][] = [
  [55, 25],
  [51, 25.3],
  [48, 29.3],
  [36, 31],
  [44, 33.3],
  [67, 24.8],
  [39, 15],
  [45, 2],
  [36.8, -1.3],
  [28, -26],
  [3, 6.5],
  [101.7, 3.1],
  [104, 1.3],
  [121, 14.6],
  [90.4, 23.8],
  [85, 27.7],
];

export type FootprintFigureIcon =
  | "facilities"
  | "sectors"
  | "countries"
  | "revenue";

export type FootprintFigure = {
  value: string;
  /** Translation key for the text after the value — see `KeyFigure.suffix`. */
  suffix?: string;
  label: string;
  icon: FootprintFigureIcon;
};

export type FootprintCountry = {
  key: string;
  /**
   * ISO 3166-1 numeric code, as a string — matches `id` on the features in
   * `public/geo/countries-110m.json`, which is how the globe finds the
   * landmass to highlight while this country is selected.
   */
  topoId: string;
  /** Translation key for the country name. */
  name: string;
  /** Translation key for the approved country paragraph. */
  body: string;
  /** [longitude, latitude] in degrees. */
  coords: [number, number];
  /**
   * The home market carries the manufacturing accent colour permanently; the
   * other markers only take it while they are the selected country.
   */
  isHeadquarters?: boolean;
};

export const footprintCountries: FootprintCountry[] = [
  {
    key: "egypt",
    topoId: "818",
    name: "globalFootprint.countries.egypt.name",
    body: "globalFootprint.countries.egypt.body",
    coords: [30, 27],
  },
  {
    key: "saudiArabia",
    topoId: "682",
    name: "globalFootprint.countries.saudiArabia.name",
    body: "globalFootprint.countries.saudiArabia.body",
    coords: [45, 24],
    isHeadquarters: true,
  },
  {
    key: "india",
    topoId: "356",
    name: "globalFootprint.countries.india.name",
    body: "globalFootprint.countries.india.body",
    coords: [79, 22],
  },
  {
    key: "vietnam",
    topoId: "704",
    name: "globalFootprint.countries.vietnam.name",
    body: "globalFootprint.countries.vietnam.body",
    coords: [108.5, 14],
  },
];
